// src/roles/store/ReceiveMaterial.js
// v9 — D-291 (Stabilization P3d-4): goods and bill are two events.
//   - The GRN writes stock + receipt + PO lock only. NO vendor payable: the
//     receipt is saved "awaiting_bill" and the vendor's bill is entered in
//     Awaiting Bills (purchaseBillService.js), which creates the payable.
//   - Rate pre-fill = stockRateFromPOLine(): rate before GST when the company
//     claims ITC (the GST comes back as credit), incl. GST when it does not.
//   - Counted units (Nos, Set ...) take whole numbers only (units.js,
//     buildStoreGRN badQty).
//
// v8 — D-292 (Stabilization P1): the whole store GRN in ONE transaction.
//
// CHANGES vs v7:
//   - Submit and Short Close share ONE path (runGRN) and ONE pure builder
//     (src/utils/grnPlan.js buildStoreGRN) — before, they were two copies
//     that had already drifted.
//   - Everything is saved by purchaseReceiptService.completeStoreGRN() in a
//     single transaction: stock + ledger rows (real stock before / after),
//     receipt, PO lock (vendor bill: see v9 above). A second tab, a double click or a retry
//     is refused; a failure or offline submit writes nothing.
//   - Lines are keyed by their position in the PO (_rowKey / _poIndex), not
//     by item_id — a PO that lists the same item twice no longer mixes the
//     two lines' quantities and rates.
//   - Item matching (CORE_RULES §81): a line whose ITEM- id is not in Item
//     Master is blocked — never guessed by code or name. Extra items must be
//     found by item_id (before, an unmatched extra item was billed but its
//     stock was silently never credited).
//   - Unit conversion converts the rate as well as the qty.
//   - Rate is required only for lines actually received.
//   - Item Master list includes legacy company_id "" items (when readable).
//   - A Direct Site Purchase PO cannot be received here (it is received at
//     site — Confirm → Finalize).
//
// v7 — Root-cause hardening: fresh Item Master fetch + block-on-mismatch.

import { useEffect, useState } from "react";
import { db } from "../../firebase";
import {
  doc, getDoc, getDocs,
  collection, query, where,
} from "firebase/firestore";
import { useParams, useNavigate } from "react-router-dom";
import { completeStoreGRN }   from "../../services/purchaseReceiptService";
import {
  buildStoreGRN,
  hasGRNErrors,
  poFingerprint,
  stockRateFromPOLine,
} from "../../utils/grnPlan";
import { companyClaimsItc, itcOnPurchase } from "../../utils/gstBill";
import { qtyStepFor } from "../../utils/units";

const userData = () => JSON.parse(localStorage.getItem("user") || "{}");

// Roles allowed to confirm Short Close (unchanged by D-292 — see open
// question: the approval matrix says Manager / SM / Owner).
const CAN_SHORT_CLOSE = ["manager", "owner", "sm", "store"];

export default function ReceiveMaterial() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const me       = userData();

  const [po,          setPo]          = useState(null);
  const [items,       setItems]       = useState([]);
  const [itemsMaster, setItemsMaster] = useState([]);
  const [submitting,  setSubmitting]  = useState(false);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);

  // Fingerprint of the PO lines when this page opened. The service refuses
  // the GRN if the PO's lines changed since (amend / cancel in another tab).
  const [loadedFingerprint, setLoadedFingerprint] = useState("");

  // D-291 P3d-4: does the company claim GST input credit? Decides whether
  // the rate pre-fill is before GST (yes) or incl. GST (no).
  const [itcClaimed, setItcClaimed] = useState(false);
  const [vendorBlocksItc, setVendorBlocksItc] = useState(false); // P3d-4c

  const [newItem,     setNewItem]     = useState({
    item_id: "", item_code: "", item_name: "",
    category: "", receive_now_qty: "", unit: "nos",
  });
  // GRN rate per screen line, keyed by the line's _rowKey.
  const [itemRates,   setItemRates]   = useState({});
  const [suggestions, setSuggestions] = useState([]);

  // Actual Freight (optional, whole-PO) — reference only. Stored next to the
  // PO's own freight estimate, never overwrites it, never in any total.
  const [actualFreight, setActualFreight] = useState("");

  // Short Close modal state
  const [showShortClose,   setShowShortClose]   = useState(false);
  const [shortCloseReason, setShortCloseReason] = useState("");
  const [shortClosing,     setShortClosing]     = useState(false);
  const [shortItems,       setShortItems]       = useState([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadItemsMaster(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (id) fetchPO(); }, [id]);

  // Fresh Item Master list — this company's items plus legacy items saved
  // before multi-tenancy (company_id ""). The legacy read is optional: if
  // the rules do not allow it, the GRN still works for the company's own
  // items and a line that needs a legacy item is blocked with a message.
  const fetchItemsMasterList = async () => {
    const cid  = me.company_id || "";
    const own  = await getDocs(query(collection(db, "items"), where("company_id", "==", cid)));
    let legacyDocs = [];
    try {
      const legacy = await getDocs(query(collection(db, "items"), where("company_id", "==", "")));
      legacyDocs = legacy.docs;
    } catch (e) {
      console.warn("[ReceiveMaterial] legacy items (company_id \"\") not readable — skipped:", e?.code || e);
    }
    const master = [];
    const seen   = new Set();
    for (const d of [...own.docs, ...legacyDocs]) {
      if (seen.has(d.id)) continue;
      seen.add(d.id);
      const item = { id: d.id, ...d.data() };
      if (!item.item_name?.trim()) continue;
      master.push(item);
    }
    return master;
  };

  const loadItemsMaster = async () => {
    try {
      const master = await fetchItemsMasterList();
      setItemsMaster(master);
    } catch (err) {
      console.error("[ReceiveMaterial] loadItemsMaster error:", err);
    }
  };

  const fetchPO = async () => {
    setLoading(true);
    setError(null);
    try {
      const snap = await getDoc(doc(db, "purchase_orders", id));
      if (!snap.exists()) { setError("Purchase order not found."); setLoading(false); return; }

      const poData = { id: snap.id, ...snap.data() };

      if (poData.company_id !== me.company_id) {
        setError("Access denied."); setLoading(false); return;
      }
      if (poData.purchase_mode === "direct_site_purchase") {
        setError("This is a Direct Site Purchase — it is received at site (Confirm → Finalize), not here.");
        setLoading(false); return;
      }
      if (poData.grn_locked) {
        setError("GRN already completed for this PO."); setLoading(false); return;
      }
      if (poData.po_status !== "ordered") {
        setError(`GRN not allowed — PO is "${poData.po_status}". Must be Ordered first.`);
        setLoading(false); return;
      }

      // D-291 P3d-4 / P3d-4c: rate before GST only when the company claims
      // ITC (Company Profile) AND this vendor's GSTIN is valid (Vendor Master).
      // Anything unreadable -> incl. GST (never claim credit by mistake).
      let itc = false;
      let vendorBlocks = false;
      try {
        const cSnap = await getDoc(doc(db, "companies", me.company_id));
        const company = cSnap.exists() ? cSnap.data() : null;
        let vendor = null;
        if (poData.vendor_id) {
          const vSnap = await getDoc(doc(db, "vendors", poData.vendor_id));
          const v = vSnap.exists() ? vSnap.data() : null;
          vendor = v && v.company_id === me.company_id ? v : null;
        }
        itc = itcOnPurchase(company, vendor);
        vendorBlocks = companyClaimsItc(company) && !itc;
      } catch (e) {
        console.warn("[ReceiveMaterial] company / vendor not readable - rate pre-fill incl. GST:", e?.code || e);
      }
      setItcClaimed(itc);
      setVendorBlocksItc(vendorBlocks);

      setPo(poData);
      setLoadedFingerprint(poFingerprint(poData.items || []));

      // One screen line per non-cancelled PO line. _poIndex = its position
      // in po.items; _rowKey keys its qty and rate on this screen.
      // Received qty pre-filled = ordered qty (store adjusts if less came).
      const mapped = (poData.items || [])
        .map((item, idx) => ({ item, idx }))
        .filter(({ item }) => !item._cancelled)
        .map(({ item, idx }) => ({
          ...item,
          _poIndex:        idx,
          _rowKey:         `po-${idx}`,
          receive_now_qty: Number(item.ordered_qty || 0),
        }));
      setItems(mapped);

      // D-304 C / D-291 P3d-4: prefill each line's GRN rate from the PO —
      // after discount, before GST when the company claims ITC (incl. GST
      // when not), excl. freight. Editable; the GRN rate stays the stock rate.
      const prefill = {};
      for (const it of mapped) {
        const r = stockRateFromPOLine(it, { itcClaimed: itc });
        if (r > 0) prefill[it._rowKey] = String(r);
      }
      setItemRates(prefill);
    } catch (err) {
      console.error("[ReceiveMaterial] fetchPO error:", err);
      setError("Failed to load purchase order.");
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestions = (val) => {
    if (!val?.trim()) { setSuggestions([]); return; }
    setSuggestions(
      itemsMaster
        .filter(i => i.item_name?.toLowerCase().includes(val.toLowerCase()))
        .slice(0, 8)
    );
  };

  const addExtraItem = () => {
    if (!newItem.item_name?.trim()) { alert("Select an item first."); return; }
    if (!String(newItem.item_id || "").trim()) {
      alert("Select the item from the search list (an item typed by hand cannot be added to stock).");
      return;
    }
    if (Number(newItem.receive_now_qty || 0) <= 0) { alert("Enter qty > 0."); return; }

    const existsIdx = items.findIndex(i =>
      String(i.item_id || "").trim() === String(newItem.item_id).trim()
    );
    if (existsIdx >= 0) {
      const updated = [...items];
      updated[existsIdx] = {
        ...updated[existsIdx],
        receive_now_qty:
          Number(updated[existsIdx].receive_now_qty || 0) + Number(newItem.receive_now_qty),
      };
      setItems(updated);
    } else {
      setItems([...items, {
        ...newItem,
        received_qty: 0, ordered_qty: 0,
        receive_now_qty: Number(newItem.receive_now_qty),
        _extra:  true,
        _rowKey: `extra-${Date.now()}`,
      }]);
    }
    setNewItem({ item_id: "", item_code: "", item_name: "", category: "", receive_now_qty: "", unit: "nos" });
    setSuggestions([]);
  };

  const rateKeyOf = (line) => line._rowKey;

  const linesMissingRate = () =>
    items.filter(line =>
      Number(line.receive_now_qty || 0) > 0 &&
      !(Number(itemRates[rateKeyOf(line)] || 0) > 0)
    );

  // ─────────────────────────────────────────────────────────
  // ONE GRN PATH for Submit and Short Close.
  // Returns the service result, or null if the GRN was stopped with a
  // message (nothing written).
  // ─────────────────────────────────────────────────────────
  const runGRN = async (mode, reason) => {
    // Fresh PO — quick checks for a clear message; the service re-checks
    // all of this inside the transaction.
    const poSnap = await getDoc(doc(db, "purchase_orders", id));
    if (!poSnap.exists()) throw new Error("PO not found");
    const poData = poSnap.data();

    if (poData.grn_locked || poData.po_status !== "ordered") {
      throw new Error("This PO has already been received (or is no longer Ordered). Please reload the page.");
    }
    if (poFingerprint(poData.items || []) !== loadedFingerprint) {
      throw new Error("This PO was changed after you opened this page. Please reload and check the quantities again.");
    }

    // Fresh Item Master right before matching (never the mount-time list).
    const freshItemsMaster = await fetchItemsMasterList();
    setItemsMaster(freshItemsMaster);

    const plan = buildStoreGRN({
      poItems:    poData.items || [],
      uiLines:    items,
      rates:      itemRates,
      masterList: freshItemsMaster,
      mode,
      reason,
    });

    if (hasGRNErrors(plan.errors)) {
      const parts = [];
      if (plan.errors.unmatched.length > 0) {
        parts.push(
          "These item(s) could not be matched to Item Master, so their stock cannot be credited:\n" +
          plan.errors.unmatched.map(n => `• ${n}`).join("\n") +
          "\nCheck them in Item Master — the item may be missing, or several items may share the same name/code."
        );
      }
      if (plan.errors.missingRate.length > 0) {
        parts.push(
          "Rate missing for:\n" +
          plan.errors.missingRate.map(n => `• ${n}`).join("\n")
        );
      }
      if (plan.errors.shortInFull) {
        parts.push("Some items are short — use Short Close.");
      }
      if (plan.errors.nothingReceived) {
        parts.push("Nothing is received. Enter the quantities that arrived, or use Short Close.");
      }
      if ((plan.errors.badQty || []).length > 0) {
        parts.push(
          "Quantity not allowed:\n" +
          plan.errors.badQty.map(n => `• ${n}`).join("\n")
        );
      }
      alert(`${mode === "short_close" ? "Short Close" : "GRN"} blocked — nothing was saved.\n\n${parts.join("\n\n")}`);
      return null;
    }

    const result = await completeStoreGRN({
      purchase_order_id:  id,
      company_id:         poData.company_id || "",
      po_fingerprint:     loadedFingerprint,
      updatedItems:       plan.updatedItems,
      stockLines:         plan.stockLines,
      receipt_type:       mode,
      short_close_reason: reason,
      completed_by:       me.uid  || me.id || "",
      completed_by_name:  me.name || "",
      actualFreight:      Number(actualFreight || 0),
    });

    // Resolve the po_ordered notification (non-blocking).
    try {
      const { markNotificationReadByReference } = await import("../../services/notificationService");
      await markNotificationReadByReference(id, "po_ordered", poData.company_id || "");
    } catch (e) {
      console.warn("[GRN] resolve notification failed (non-blocking):", e);
    }

    return { ...result, poNumber: poData.po_number || id };
  };

  // ─────────────────────────────────────────────────────────
  // SUBMIT — no shorts allowed here (short → Short Close modal)
  // ─────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (submitting) return;

    const missing = linesMissingRate();
    if (missing.length > 0) {
      alert(`Rate missing for: "${missing[0].item_name}"\nPlease enter the rate (₹) for every item received.`);
      return;
    }

    const detectedShortItems = items.filter(item => {
      const ordered    = Number(item.ordered_qty || 0);
      const receiveNow = Number(item.receive_now_qty || 0);
      return !item._extra && ordered > 0 && receiveNow < ordered;
    });
    if (detectedShortItems.length > 0) {
      setShortItems(detectedShortItems);
      setShortCloseReason("");
      setShowShortClose(true);
      return;
    }

    setSubmitting(true);
    try {
      const result = await runGRN("full", "");
      if (!result) return;
      const msg = result.has_over
        ? "⚠ Over-receive flagged — Manager review required"
        : "All items received correctly ✅";
      const billMsg = result.bill_status === "awaiting_bill"
        ? "\n\nNext: when the vendor's bill comes, press 'Enter bill' on the PO page - the bill adds it to the vendor's khaata."
        : "";
      alert(`GRN Submitted ✅\nPO: ${result.poNumber}\n${msg}${billMsg}`);
      navigate(`/purchase-orders/${id}`, { replace: true });
    } catch (err) {
      console.error("[GRN] error:", err);
      alert(err.message || "GRN failed. Nothing was saved — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ─────────────────────────────────────────────────────────
  // SHORT CLOSE — mixed short + over in the same PO
  // ─────────────────────────────────────────────────────────
  const handleShortClose = async () => {
    if (shortClosing) return;
    const reason = shortCloseReason.trim();
    if (!reason) { alert("Reason is mandatory."); return; }

    const role = (me.role || "").toLowerCase().trim();
    if (!CAN_SHORT_CLOSE.includes(role)) {
      alert("Only Manager / SM / Owner can confirm Short Close.");
      return;
    }

    const missing = linesMissingRate();
    if (missing.length > 0) {
      alert(`Rate missing for: "${missing[0].item_name}"\nPlease enter the rate (₹) for every item received.`);
      return;
    }

    setShortClosing(true);
    try {
      const result = await runGRN("short_close", reason);
      if (!result) return;
      setShowShortClose(false);
      const billMsg = result.bill_status === "awaiting_bill"
        ? "\n\nNext: when the vendor's bill comes, press 'Enter bill' on the PO page - the bill adds it to the vendor's khaata."
        : "";
      alert(`✅ PO Short Closed\nReceived items added to inventory.\nCancelled qty: ${result.total_cancelled_qty} units permanently closed.${billMsg}`);
      navigate(`/purchase-orders/${id}`, { replace: true });
    } catch (err) {
      console.error("[ShortClose] error:", err);
      alert(err.message || "Short close failed. Nothing was saved — try again.");
    } finally {
      setShortClosing(false);
    }
  };

  // ─────────────────────────────────────────────────────────
  // UI
  // ─────────────────────────────────────────────────────────

  const canShortClose = CAN_SHORT_CLOSE.includes((me.role || "").toLowerCase().trim());

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <p className="text-gray-500 text-sm">Loading GRN...</p>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 p-6 max-w-2xl mx-auto">
      <button onClick={() => navigate(-1)} className="mb-4 text-sm text-slate-500 hover:text-slate-800">← Back</button>
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-700 font-medium">{error}</p>
        <button onClick={() => navigate("/purchase-orders")}
          className="mt-4 px-4 py-2 bg-gray-200 rounded-lg text-sm hover:bg-gray-300">
          Go to Purchase Orders
        </button>
      </div>
    </div>
  );

  if (!po) return null;

  return (
    <div className="min-h-screen bg-gray-50 p-4 max-w-3xl mx-auto">
      <button onClick={() => navigate(-1)} className="mb-4 text-sm text-slate-500 hover:text-slate-800">← Back</button>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">

        {/* HEADER */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-xl font-bold text-slate-800">Receive Material (GRN)</h2>
            <p className="text-xs text-slate-400 mt-0.5">PO: {po.po_number || id} • {po.vendor_name || ""}</p>
          </div>
          {/* Manual Short Close — for when SM wants to close proactively */}
          {canShortClose && (
            <button
              onClick={() => {
                const detected = items.filter(i =>
                  Number(i.ordered_qty || 0) > 0 &&
                  Number(i.receive_now_qty || 0) < Number(i.ordered_qty || 0)
                );
                setShortItems(detected);
                setShortCloseReason("");
                setShowShortClose(true);
              }}
              className="px-4 py-2 rounded-xl bg-amber-100 text-amber-700 text-sm font-semibold hover:bg-amber-200">
              ⚠ Short Close PO
            </button>
          )}
        </div>

        {/* GRN RULES INFO BOX — correct message */}
        <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-5 text-xs text-green-800">
          <strong>GRN Rules:</strong> Enter exact quantities received. If any item is less than
          ordered, Short Close is mandatory — remaining qty will be permanently cancelled.
          Over-receive is allowed and will be flagged for Manager review.
          <br />
          <strong>Rate:</strong> {itcClaimed
            ? "before GST (your company claims GST input credit)"
            : vendorBlocksItc
              ? "including GST (this vendor's GSTIN is missing or not valid, so GST input credit cannot be claimed - fix it in Vendor Master if the vendor is registered)"
              : "including GST (your company does not claim GST input credit)"} — filled
          from the PO. The vendor's bill is entered after the GRN, in Awaiting Bills.
        </div>

        {/* ACTUAL FREIGHT — optional, whole-PO. The PO's own freight
            estimate (entered at creation time) rarely matches what's
            actually confirmed by the transporter/vendor invoice at
            delivery. This is purely a reference figure — never touches
            grand_total, the vendor-payable financial_transactions entry,
            or any inventory value. Leave blank if unknown; the PO's
            original estimate stays untouched either way. */}
        {po.freight_amount != null && (
          <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-3 mb-5">
            <p className="text-xs text-cyan-800 mb-2">
              <strong>Actual Freight (optional)</strong> — PO estimate was
              ₹{Number(po.freight_amount || 0).toLocaleString("en-IN")}. If the real transporter/
              vendor invoice confirms a different amount, enter it here for reference — this never
              changes the PO's original estimate.
            </p>
            <div className="flex items-center gap-2">
              <span className="text-xs text-cyan-700">₹</span>
              <input
                type="number" min="0" step="0.01"
                value={actualFreight}
                onChange={e => setActualFreight(e.target.value)}
                placeholder={String(po.freight_amount || 0)}
                className="w-40 border border-cyan-300 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-cyan-400 bg-white"
              />
            </div>
          </div>
        )}

        {/* ITEMS LIST */}
        <div className="space-y-3 mb-6">
          {items.map((item, i) => {
            const ordered    = Number(item.ordered_qty  || 0);
            const receiveNow = Number(item.receive_now_qty || 0);
            const isExtra    = item._extra || ordered === 0;
            const isOver     = ordered > 0 && receiveNow > ordered;
            const isExact    = ordered > 0 && receiveNow === ordered;
            const isShort    = ordered > 0 && receiveNow < ordered;

            return (
              <div key={item._rowKey || i} className={`border rounded-2xl p-4 ${
                isOver  ? "border-amber-300 bg-amber-50"   :
                isExtra ? "border-purple-200 bg-purple-50" :
                isExact ? "border-green-300 bg-green-50"   :
                isShort ? "border-red-200 bg-red-50"       :
                          "border-slate-200 bg-white"
              }`}>
                <div className="flex justify-between items-start">
                  <div className="flex-1 mr-3">
                    <p className="font-semibold text-slate-800 text-sm">{item.item_name}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{item.unit || "nos"}</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {ordered > 0 && (
                        <span className="text-xs text-slate-400">Ordered: {ordered}</span>
                      )}
                      {item.is_custom_item && (
                        <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full">⚡ Custom</span>
                      )}
                      {isExtra && !item.is_custom_item && (
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">Extra</span>
                      )}
                      {isOver && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                          ⚠ Over +{receiveNow - ordered}
                        </span>
                      )}
                      {isExact && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">✓ Exact</span>
                      )}
                      {isShort && (
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">
                          ✕ Short — {ordered - receiveNow} will be cancelled
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    {/* Qty */}
                    <div className="flex items-center gap-1">
                      <input
                        type="number" min="0" step={qtyStepFor(item.unit)}
                        value={item.receive_now_qty ?? 0}
                        onChange={e => {
                          const qty = Math.max(0, Number(e.target.value));
                          setItems(prev => prev.map((row, idx) =>
                            idx === i ? { ...row, receive_now_qty: qty } : row
                          ));
                        }}
                        className={`w-20 border rounded-xl px-2 py-1.5 text-right text-sm outline-none focus:ring-2 ${
                          isShort ? "border-red-300 bg-red-50 focus:ring-red-300" :
                          isOver  ? "border-amber-300 focus:ring-amber-300" :
                                    "border-slate-200 focus:ring-blue-300"
                        }`}
                      />
                      <span className="text-xs text-slate-400 w-8">{item.unit || "nos"}</span>
                    </div>
                    {/* ✅ Rate — mandatory at GRN */}
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-slate-400">₹</span>
                      <input
                        type="number" min="0" placeholder="Rate *"
                        value={itemRates[item._rowKey] || ""}
                        onChange={e => setItemRates(prev => ({
                          ...prev,
                          [item._rowKey]: e.target.value,
                        }))}
                        className="w-24 border border-slate-200 rounded-xl px-2 py-1.5 text-right text-sm outline-none focus:ring-2 focus:ring-cyan-300"
                      />
                      {(() => {
                        const poRate  = stockRateFromPOLine(item, { itcClaimed });
                        const entered = Number(itemRates[item._rowKey] || 0);
                        if (!(poRate > 0) || !(entered > 0) || Math.abs(entered - poRate) / poRate < 0.005) return null;
                        const pct = ((entered - poRate) / poRate) * 100;
                        return (
                          <span className="text-[11px] bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full whitespace-nowrap">
                            PO ₹{poRate.toLocaleString("en-IN")} → {pct > 0 ? "+" : ""}{pct.toFixed(1)}%
                          </span>
                        );
                      })()}
                      {/* Amount preview */}
                      {Number(itemRates[item._rowKey] || 0) > 0 && (
                        <span className="text-xs text-slate-500 whitespace-nowrap">
                          = ₹{(
                            Number(item.receive_now_qty || 0) *
                            Number(itemRates[item._rowKey] || 0)
                          ).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* ADD EXTRA ITEM */}
        <div className="mb-6 border border-dashed border-slate-300 rounded-xl p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase mb-3">Add Extra Item (not in PO)</p>
          <div className="flex gap-2 items-center flex-wrap">
            <div className="flex-1 relative min-w-[160px]">
              <input
                type="text" autoComplete="off" placeholder="Search item..."
                value={newItem.item_name}
                onChange={e => {
                  setNewItem({ ...newItem, item_name: e.target.value, item_id: "" });
                  fetchSuggestions(e.target.value);
                }}
                className="w-full border border-slate-200 rounded-xl p-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-300"
              />
              {suggestions.length > 0 && (
                <div className="absolute bg-white border rounded-xl shadow-lg w-full z-50 mt-1 max-h-40 overflow-y-auto">
                  {suggestions.map((s, idx) => (
                    <div key={idx}
                      onClick={() => {
                        setNewItem({
                          item_id: s.item_id || s.id || "", item_code: s.item_code || "",
                          item_name: s.item_name, category: s.category || s.item_category || "",
                          receive_now_qty: "", unit: s.unit || "nos",
                        });
                        setSuggestions([]);
                      }}
                      className="px-4 py-2 hover:bg-blue-50 cursor-pointer text-sm">
                      {s.item_name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <input type="number" min="0" step={qtyStepFor(newItem.unit)} placeholder="Qty" value={newItem.receive_now_qty}
              onChange={e => setNewItem({ ...newItem, receive_now_qty: e.target.value })}
              className="w-20 border border-slate-200 rounded-xl p-2.5 text-sm text-center outline-none" />
            <button onClick={addExtraItem}
              className="bg-blue-600 text-white px-4 py-2.5 rounded-xl text-sm font-semibold">
              + Add
            </button>
          </div>
        </div>

        {/* SUBMIT BUTTON */}
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full py-3 rounded-xl bg-green-600 text-white font-semibold text-sm disabled:opacity-40 hover:bg-green-700 transition">
          {submitting ? "Processing GRN..." : "✓ Submit GRN & Update Inventory"}
        </button>

        {/* Short items preview — shown below button if any detected */}
        {items.some(i => Number(i.ordered_qty || 0) > 0 && Number(i.receive_now_qty || 0) < Number(i.ordered_qty || 0)) && (
          <div className="mt-3 bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-700">
            <strong>⚠ Short items detected — Short Close will be required on submit:</strong>
            <ul className="mt-1 space-y-0.5">
              {items
                .filter(i => Number(i.ordered_qty || 0) > 0 && Number(i.receive_now_qty || 0) < Number(i.ordered_qty || 0))
                .map((i, idx) => (
                  <li key={idx}>
                    • {i.item_name}: received {i.receive_now_qty} of {i.ordered_qty}
                    &nbsp;→ <span className="font-semibold">{Number(i.ordered_qty) - Number(i.receive_now_qty)} will be cancelled</span>
                  </li>
                ))
              }
            </ul>
          </div>
        )}
      </div>

      {/* ── SHORT CLOSE MODAL — force triggered on short detect ── */}
      {showShortClose && (
        <div className="fixed inset-0 bg-black/60 flex items-end justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-5 max-h-[90vh] overflow-y-auto">

            <h3 className="font-bold text-slate-800 text-base mb-1">⚠ Short Delivery Detected</h3>
            <p className="text-xs text-slate-500 mb-3">
              Neeche items ordered se kam aaye hain. Remaining qty permanently cancel hogi.
            </p>

            {/* Short items summary */}
            {shortItems.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-3 space-y-1">
                {shortItems.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-xs">
                    <span className="font-medium text-slate-700">{item.item_name}</span>
                    <span className="text-red-700 font-semibold">
                      {item.receive_now_qty} / {item.ordered_qty} received
                      &nbsp;— <span className="underline">{Number(item.ordered_qty) - Number(item.receive_now_qty)} cancelled</span>
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3 text-xs text-amber-800">
              <strong>This will:</strong><br />
              • Add received quantities to inventory<br />
              • Permanently cancel remaining quantities<br />
              • Lock this PO — no future GRN possible<br />
              • Dobara chahiye to naya PO raise karna hoga
            </div>

            {/* Role check warning */}
            {!canShortClose && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-3 text-xs text-red-700 font-semibold">
                ⛔ Only Manager / SM / Owner can confirm Short Close.
                Please call your Manager to proceed.
              </div>
            )}

            <textarea
              rows={3}
              placeholder="Reason for short close * (mandatory)"
              value={shortCloseReason}
              onChange={e => setShortCloseReason(e.target.value)}
              autoFocus
              disabled={!canShortClose}
              className="w-full border border-slate-200 rounded-xl p-3 text-sm resize-none outline-none mb-3 disabled:opacity-50"
            />

            <div className="flex gap-3">
              <button
                onClick={() => { setShowShortClose(false); setShortCloseReason(""); }}
                className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-600 font-semibold text-sm">
                ← Go Back
              </button>
              <button
                onClick={handleShortClose}
                disabled={shortClosing || !canShortClose || !shortCloseReason.trim()}
                className="flex-1 py-3 rounded-xl bg-red-500 text-white font-semibold text-sm disabled:opacity-40">
                {shortClosing ? "Processing..." : "Confirm Short Close"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}